/* eslint-disable react-hooks/set-state-in-effect -- this editor intentionally syncs local edit state from effects. */
import * as React from "react";
import styled, {css} from "styled-components";
import {createRef, useCallback, useEffect, useState} from "react";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {FieldDataType} from "../../../../generated/api/base";
import {ReactUtils} from "../../../utils/ReactUtils";
import {type IInputType} from "../../details/datatypes/TextInputV5";
import {FieldInputsV5} from "../../../widgets/input/clicktoedit/InputUtils";
import {SingleLineInputV5} from "../../details/datatypes/SingleLineInputV5";
import {InfoBubbleV5} from "../../button/InfoBubbleV5";
import ErrorInfoIcon from "../../icons/errorInfo.svg?react";
import {colorPalette} from "../../styles/colorPalette";
import {FlexCenterStyle, fontSize, InfoIconStyled, radius} from "../../styles/styles";
import {Spinner, SpinnerStyled} from "../../loader/Spinner";

interface IClickToEditInputProps {
	readonly className?: "disabledInputField" | string;
	readonly dataTypeSettings?: any; // {decimals: number, formatting: "currency", maximum: number, minimum: number}
	readonly dataType?: FieldDataType;
	readonly value: any;
	readonly placeholder?: any;
	readonly trimText?: boolean;
	readonly disabled?: boolean;
	readonly isUpdating?: boolean;
	readonly hasValidation?: boolean;
	readonly focused?: boolean;
	readonly dateFormat?: string;
	readonly onFocusLossForceBlur?: boolean;
	readonly inputType?: IInputType;
	readonly caretPosition?: number;
	readonly inline?: boolean;
	readonly onLiveChange?: (value: any) => void;
	readonly onCancelEdit?: () => void;
	readonly onChange?: (value: any) => void;
	readonly getErrorMessage?: (value: any) => string; // returns an errormessage (or empty string, if no errors)
	readonly showErrorOnlyIfEdited?: boolean;
	readonly onHover?: (over?: boolean) => void;
	readonly onBlur?: () => void;
	readonly onFocus?: () => void;
}

export const ClickToEditInputV5 = (props: IClickToEditInputProps) => {
	const {
		className,
		dataTypeSettings, // {decimals: number, formatting: "currency", maximum: number, minimum: number}
		dataType = FieldDataType.SingleLineText,
		value = null,
		placeholder,
		trimText = true,
		disabled,
		isUpdating = false,
		hasValidation,
		focused,
		dateFormat,
		onFocusLossForceBlur,
		inputType,
		caretPosition,
		inline,
		onLiveChange,
		onCancelEdit,
		onChange,
		getErrorMessage = (value: any) => "", // returns an errormessage (or empty string, if no errors)
		showErrorOnlyIfEdited = true,
		onHover,
		onBlur,
		onFocus,
	} = props;

	const _infoIconRef = createRef<HTMLDivElement>();
	const _element = createRef<HTMLDivElement>();

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isFocusingInfoIcon, setIsFocusingInfoIcon] = useState<boolean>(false);
	const [hasBeenEditedSinceSave, setHasBeenEditedSinceSave] = useState<boolean>(false);

	const onCancelEditInInput = useCallback(() => {
		onLiveChange?.(value);
		onCancelEdit?.();
		setIsEditing(false);
	}, [onCancelEdit, onLiveChange, value]);

	const trimValueIfNeeded = useCallback(
		(editingValue: string | number) => {
			if (trimText && typeof editingValue === "string") {
				editingValue = editingValue.trim();
			}

			return editingValue;
		},
		[trimText],
	);

	const getEditingValue = useCallback(() => {
		return trimValueIfNeeded(value);
	}, [trimValueIfNeeded, value]);

	const onApplyEdit = useCallback(() => {
		if (isEditing) {
			const editingValue = getEditingValue();
			setIsEditing(false);
			if (!getErrorMessage(editingValue)) {
				onChange?.(editingValue);
				return true;
			}

			return false;
		}
	}, [getEditingValue, getErrorMessage, onChange, isEditing]);

	const onEditingInput = (value: any) => {
		setHasBeenEditedSinceSave(true);
		onLiveChange?.(value);
	};

	const onChangeInInput = (value: any) => {
		if (isEditing) {
			const newValue = trimValueIfNeeded(value);
			onChange?.(newValue);
			setIsEditing(false);
		}
	};

	const onBlurInInput = () => {
		onBlur?.();
		setIsFocusingInfoIcon(false);
		setIsEditing(false);
	};

	const handleFocus = () => {
		setIsFocusingInfoIcon(true);
	};

	const getEditInput = () => {
		let InputComponent = FieldInputsV5[dataType];

		InputComponent = InputComponent || SingleLineInputV5;
		return (
			<InputComponent
				value={value ?? dateFormat}
				dataTypeSettings={dataTypeSettings}
				onInput={onEditingInput}
				onChange={onChangeInInput}
				onBlur={onBlurInInput}
				onFocusLossForceBlur={onFocusLossForceBlur}
				inputType={inputType}
				caretPosition={caretPosition}
				focused={focused}
				disabled={disabled}
				placeholder={placeholder}
			/>
		);
	};

	const onMouseOver = () => {
		onHover?.(true);
	};

	const onMouseLeave = () => {
		onHover?.(false);
	};

	const turnFocusOn = useCallback(() => {
		setIsFocusingInfoIcon(true);
	}, []);

	const turnFocusOff = useCallback(() => {
		setIsFocusingInfoIcon(false);
	}, []);

	const onClick = () => {
		setIsEditing(true);
		onFocus?.();
	};

	useEffect(() => {
		const onKeyUp = (event: KeyboardEvent) => {
			switch (event.key) {
				case KeyboardListener.KEY_ESCAPE:
					onCancelEditInInput();
					break;
				case KeyboardListener.KEY_ENTER:
					if (!(dataType === FieldDataType.MultiLineText && KeyboardListener.isShiftDown)) {
						onApplyEdit();
					}
					break;
			}
		};

		KeyboardListener.getInstance().signals.up.add(onKeyUp);

		return () => {
			KeyboardListener.getInstance().signals.up.remove(onKeyUp);
		};
	}, [dataType, onApplyEdit, onCancelEditInInput]);

	useEffect(() => {
		if (focused === false) {
			onApplyEdit();
		}
	}, [focused, onApplyEdit]);

	const editingValue = getEditingValue();
	const errorMessage = getErrorMessage(editingValue);
	const showErrorMessage = (!showErrorOnlyIfEdited || (showErrorOnlyIfEdited && hasBeenEditedSinceSave)) && errorMessage && !disabled;

	return (
		<ClickToEditInputStyled
			className={className || ""}
			ref={_element}
			onMouseOver={onMouseOver}
			onMouseLeave={onMouseLeave}
			onClick={onClick}
			$error={showErrorMessage}
			$empty={!getEditingValue()}
			$inline={inline}
			onFocus={handleFocus}
			onBlur={onBlurInInput}
			title={value}
		>
			{showErrorMessage && (
				<InfoIconStyled
					ref={_infoIconRef}
					className={ReactUtils.cls("infoIcon", {isFocusingInfoIcon})}
				>
					<ErrorInfoIcon
						onMouseEnter={turnFocusOn}
						onMouseLeave={turnFocusOff}
					/>
					<InfoBubbleV5
						anchorRef={_infoIconRef}
						content={errorMessage}
						isErrorMessage={true}
						hide={!isFocusingInfoIcon}
					/>
				</InfoIconStyled>
			)}
			{getEditInput()}
			<Spinner
				isVisible={isUpdating}
				isYellow={hasValidation}
			/>
		</ClickToEditInputStyled>
	);
};

export const ClickToEditInputStyled = styled.div<{$error: boolean; $empty: boolean; $inline: boolean}>`
	width: 100%;
	position: relative;
	min-height: 32px;

	${SpinnerStyled} {
		position: absolute;
		top: 8px;
		right: 7px;
	}

	> div,
	input {
		height: 100%;
	}

	.infoIcon {
		position: absolute;
		top: 0;
		right: 5px;

		svg {
			height: 16px;
			width: 16px;
			color: #ff4136;
		}
	}

	input:focus {
		border: 1px solid ${colorPalette.primary.c500Primary};
	}

	${(props) => {
		if (props.$error) {
			return css`
				.field-input-container {
					input {
						border: 1px solid red;
					}
				}

				.unfocused {
					border: 1px solid red;
				}

				border-radius: ${radius.sm};
			`;
		}
	}}

	.SingleLineLabel {
		overflow-x: hidden;
		text-overflow: ellipsis;
	}

	.field-input-container {
		align-items: stretch;
		width: 100%;

		input {
			background: transparent;
			overflow: hidden;
			white-space: nowrap;
		}
	}

	.multiline {
		textarea {
			max-height: 94px;
			overflow-y: auto;
			line-height: 16px;
			resize: none;
			margin-bottom: 0;
			padding: 10px;
			height: inherit;

			&:focus {
				background: ${colorPalette.white};
			}
		}

		&.extended,
		&.expand {
			textarea {
				max-height: 400px;
				overflow-y: auto;
			}
		}
	}

	.unfocused,
	input {
		border: ${(props) => (props.$inline ? "none" : `1px solid ${colorPalette.gray.c300}`)};
		border-radius: ${radius.sm};
		padding: 7px;
		width: 100%;
		font-size: ${fontSize.md};
		outline: none;
	}

	.unfocused {
		display: flex;
		align-items: center;
		cursor: text;
		word-break: break-all;
		min-height: 32px;

		&:hover:not(.disabled) {
			background-color: ${colorPalette.gray.c200Light};
		}

		&.disabled {
			border-color: transparent;
			cursor: auto;

			.MultiLineLabel {
				pointer-events: all;
			}
		}

		p {
			margin: 0;
			line-height: 18px;
			white-space: break-spaces;
		}

		&.isUpdating {
			.SingleLineLabel {
				width: calc(100% - 10px);
			}
		}

		&:empty {
			${FlexCenterStyle};
			height: 32px;
		}
	}

	&.noButtons {
		.infoIcon {
			right: 10px;
			width: 10px;
			height: 10px;
			position: absolute;
		}
	}

	&.infoBubbleAlignLeft {
		.InfoBubble {
			transform: translate(calc(-100% + 15px), -44px);

			&::after {
				right: 8px;
			}
		}
	}

	&.portfolionameInput {
		.error {
			left: -60px;
			&::after {
				left: auto;
				right: 5px;
			}
		}
	}

	&.disabledInputField {
		border-radius: ${radius.sm};
		color: ${colorPalette.gray.c300};
		pointer-events: none;
	}
`;
